import React, { useState, useEffect, useCallback } from 'react';
import { marketplaceApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, PiggyBank, LineChart, Coins, Users, Clock,
  CheckCircle, ExternalLink, Loader2, AlertTriangle, Wallet, Target
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  beginner: TrendingUp,
  automated: LineChart,
  retirement: Clock,
  high_yield: PiggyBank,
  micro: Coins,
  family: Users
};

const RISK_COLORS = {
  'Low': 'bg-green-100 text-green-700 border-green-200',
  'Moderate': 'bg-amber-100 text-amber-700 border-amber-200',
  'Market Risk': 'bg-red-100 text-red-700 border-red-200'
};

const BENEFIT_COLORS = {
  'Automated': 'bg-blue-100 text-blue-700',
  'No Minimum': 'bg-green-100 text-green-700',
  'Diversified': 'bg-purple-100 text-purple-700',
  'Retirement': 'bg-amber-100 text-amber-700',
  'Roundups': 'bg-teal-100 text-teal-700'
};

export default function FinancialMarketplace() {
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(function() {
    async function fetchData() {
      try {
        const [providersRes, categoriesRes] = await Promise.all([
          marketplaceApi.getFinancialProviders(),
          marketplaceApi.getFinancialCategories()
        ]);
        setProviders(providersRes.providers || []);
        setCategories(categoriesRes.categories || []);
      } catch (err) {
        console.error('Failed to fetch marketplace data:', err);
        toast.error('Failed to load marketplace');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleProviderClick = useCallback(async function(provider) {
    if (!provider.is_active || !provider.cta_url) {
      return;
    }
    
    try {
      await marketplaceApi.trackFinancialClick(provider.provider_id);
    } catch (err) {
      console.error('Failed to track click:', err);
    }
    
    let url = provider.cta_url;
    if (provider.affiliate_param) {
      const separator = url.includes('?') ? '&' : '?';
      url = url + separator + provider.affiliate_param;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const filteredProviders = selectedCategory === 'all' 
    ? providers 
    : providers.filter(function(p) { return p.category === selectedCategory; });

  function renderProviderCard(provider) {
    const IconComponent = CATEGORY_ICONS[provider.category] || Wallet;
    const isActive = provider.is_active && provider.cta_url;
    
    return (
      <Card 
        key={provider.provider_id} 
        className="border-slate-200 hover:shadow-lg transition-shadow"
        data-testid={'provider-card-' + provider.provider_id}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {provider.logo_url ? (
                <img 
                  src={provider.logo_url} 
                  alt={provider.name}
                  className="w-12 h-12 rounded-lg object-contain bg-slate-50 p-1"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-100 to-blue-100 flex items-center justify-center">
                  <IconComponent className="h-6 w-6 text-teal-600" />
                </div>
              )}
              <div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                  {provider.name}
                </CardTitle>
                <p className="text-sm text-slate-500">{provider.investment_type}</p>
              </div>
            </div>
            {provider.risk_level && (
              <Badge 
                variant="outline" 
                className={'text-xs ' + (RISK_COLORS[provider.risk_level] || 'bg-slate-100')}
              >
                {provider.risk_level}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600 line-clamp-2">{provider.description}</p>
          
          {/* Minimum to Start */}
          {provider.minimum_start && (
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-600">
                Minimum: <span className="font-medium text-slate-800">{provider.minimum_start}</span>
              </span>
            </div>
          )}
          
          {/* Benefit Tags */}
          {(provider.benefit_tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {provider.benefit_tags.map(function(tag) {
                return (
                  <Badge 
                    key={tag} 
                    className={'text-xs ' + (BENEFIT_COLORS[tag] || 'bg-slate-100 text-slate-600')}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {tag}
                  </Badge>
                );
              })}
            </div>
          )}
          
          {/* CTA Button */}
          <Button
            className={isActive ? 'w-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white' : 'w-full bg-slate-200 text-slate-500 cursor-not-allowed'}
            onClick={function() { handleProviderClick(provider); }}
            disabled={!isActive}
            data-testid={'cta-' + provider.provider_id}
          >
            {isActive ? (
              <>
                {provider.cta_label || 'Open Account'}
                <ExternalLink className="ml-2 h-4 w-4" />
              </>
            ) : (
              'Coming Soon'
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="financial-marketplace">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Financial Growth Marketplace
        </h1>
        <p className="text-slate-600 mt-1">
          Savings and investing tools for long-term financial growth
        </p>
      </div>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList className="flex-wrap h-auto gap-2 bg-transparent p-0">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-teal-100 data-[state=active]:text-teal-700"
          >
            All Options
          </TabsTrigger>
          {categories.map(function(cat) {
            const Icon = CATEGORY_ICONS[cat.id] || Wallet;
            return (
              <TabsTrigger 
                key={cat.id} 
                value={cat.id}
                className="data-[state=active]:bg-teal-100 data-[state=active]:text-teal-700"
              >
                <Icon className="h-4 w-4 mr-2" />
                {cat.name}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value={selectedCategory} className="mt-6">
          {filteredProviders.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <TrendingUp className="h-12 w-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No providers yet</h3>
                <p className="text-slate-500 mt-1">
                  Check back soon for investing options
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProviders.map(renderProviderCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Compliance Disclosure */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-slate-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-600">
              We may receive compensation from partner links. These are third-party services. 
              We do not provide lending or investment advice. Credit and financial outcomes vary.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
